import { useMemo, useState } from 'react';
import {
  validateBlockPlacement,
  validateBlockPlacementInGap,
  parseCidr,
} from '../../apps/cidr-planner/cidrUtils';
import { usableIpCount } from '../../apps/cidr-planner/usableIpUtils';
import type { AddressRange, IpCountMode } from '../../apps/cidr-planner/types';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';
import { CidrSheet } from './CidrSheet';

type AddCidrDialogProps = {
  parentCidr: string;
  existingCidrs: string[];
  ipCountMode: IpCountMode;
  lang: DisplayLang;
  onClose: () => void;
  onConfirm: (payload: { cidr: string; name: string }) => void;
  gap?: AddressRange;
  initialCidr?: string;
};

const TEXT = {
  ja: {
    title: 'CIDR を追加',
    gapTitle: '空き領域に追加',
    cidr: 'CIDR',
    name: '名前（任意）',
    normalized: 'ネットワークアドレスに正規化されます',
    usable: '使用可能 IP',
    outOfParent: '親 CIDR の範囲外です',
    outOfGap: '選択した空き領域の範囲外です',
    overlap: '既存ブロックと重複しています',
    invalid: 'CIDR 形式が正しくありません',
    awsTooSmall: 'AWS VPC では /28 より細かいサブネットは作成できません',
    cancel: 'キャンセル',
    add: '追加',
    close: '閉じる',
  },
  en: {
    title: 'Add CIDR',
    gapTitle: 'Add to free space',
    cidr: 'CIDR',
    name: 'Name (optional)',
    normalized: 'Will be normalized to the network address',
    usable: 'Usable IPs',
    outOfParent: 'Outside the parent CIDR range',
    outOfGap: 'Outside the selected free space',
    overlap: 'Overlaps an existing block',
    invalid: 'Invalid CIDR format',
    awsTooSmall: 'AWS VPC subnets cannot be smaller than /28',
    cancel: 'Cancel',
    add: 'Add',
    close: 'Close',
  },
} as const;

function mapError(
  error: string,
  t: {
    outOfParent: string;
    outOfGap: string;
    overlap: string;
    invalid: string;
    awsTooSmall: string;
  },
): string {
  if (error === 'OUT_OF_PARENT') return t.outOfParent;
  if (error === 'OUT_OF_GAP') return t.outOfGap;
  if (error === 'OVERLAP') return t.overlap;
  if (error === 'AWS_TOO_SMALL') return t.awsTooSmall;
  return t.invalid;
}

export function AddCidrDialog({
  parentCidr,
  existingCidrs,
  ipCountMode,
  lang,
  onClose,
  onConfirm,
  gap,
  initialCidr = '',
}: AddCidrDialogProps) {
  const t = TEXT[lang];
  const [cidrInput, setCidrInput] = useState(initialCidr);
  const [name, setName] = useState('');

  const validation = useMemo(() => {
    if (!cidrInput.trim()) {
      return null;
    }
    if (gap) {
      return validateBlockPlacementInGap(
        cidrInput,
        parentCidr,
        gap,
        existingCidrs,
        ipCountMode,
      );
    }
    return validateBlockPlacement(cidrInput, parentCidr, existingCidrs, undefined, ipCountMode);
  }, [cidrInput, parentCidr, existingCidrs, ipCountMode, gap]);

  const prefix = validation?.ok ? parseCidr(validation.normalized).prefix : null;

  return (
    <CidrSheet title={gap ? t.gapTitle : t.title} closeLabel={t.close} onClose={onClose}>
      <label className="cidr-sheet__field">
        <span className="cidr-sheet__label">{t.cidr}</span>
        <input
          className="cidr-sheet__input"
          value={cidrInput}
          onChange={(event) => setCidrInput(event.target.value)}
          placeholder="10.0.1.0/24"
          inputMode="decimal"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </label>
      {validation?.ok && validation.wasNormalized && (
        <p className="cidr-sheet__hint">
          {t.normalized}: {validation.normalized}
        </p>
      )}
      {validation?.ok && prefix !== null && (
        <p className="cidr-sheet__hint">
          {t.usable}: {usableIpCount(prefix, ipCountMode)}
        </p>
      )}
      {validation && !validation.ok && (
        <p className="cidr-sheet__error">{mapError(validation.error, t)}</p>
      )}
      <label className="cidr-sheet__field">
        <span className="cidr-sheet__label">{t.name}</span>
        <input
          className="cidr-sheet__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      <div className="cidr-sheet__actions">
        <button type="button" className="cidr-sheet__btn" onClick={onClose}>
          {t.cancel}
        </button>
        <button
          type="button"
          className="cidr-sheet__btn cidr-sheet__btn--primary"
          disabled={!validation?.ok}
          onClick={() => {
            if (validation?.ok) {
              onConfirm({ cidr: validation.normalized, name: name.trim() });
            }
          }}
        >
          {t.add}
        </button>
      </div>
    </CidrSheet>
  );
}
